import { useState } from "react";
import { Col } from "reactstrap";
import { motion } from "framer-motion";
import useCountUp from "hooks/useCountUp";

/**
 * A KPI stat card.
 *
 * WHAT THIS IS NOT, AND WHY
 * Two earlier versions of this card were rejected for looking templated, and
 * both times the giveaway was decoration standing in for hierarchy: first a
 * saturated animated gradient behind white text, then a coloured left rail
 * above an all-caps tracked label. Neither said anything the reader needed.
 *
 * What is left is the information in the order it is read: a short name, the
 * number, and the one line of context that makes the number mean something
 * ("of 209 recorded"). Colour appears once, small, as a tinted glyph — enough
 * to tell four cards apart at a glance, never behind text.
 *
 * `colorClass` is mapped to an accent class here rather than applied directly:
 * those names are theme-owned globals and one of them paints a gradient as a
 * background-image, which landed behind the numerals.
 */

const ACCENT_BY_COLOR_CLASS = {
  "primary-gradient-bg": "mcap-accent-teal",
  "bg-blue": "mcap-accent-green",
  "bg-purple": "mcap-accent-violet",
  "bg-orange": "mcap-accent-clay",
};

const tileVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: (index) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.4, delay: index * 0.05, ease: [0.23, 1, 0.32, 1] },
  }),
};

export default function KpiTile({
  label,
  value,
  // Renders tight against the figure ("81.5%"). Distinct from `suffix`, which
  // is the comparison beside it ("1555 of 1940") — a percent sign belongs to
  // the number, a denominator sits next to it.
  unit = "",
  suffix = "",
  colorClass = "primary-gradient-bg",
  index = 0,
  isVertical = false,
  loading = false,
  unavailableLabel = null,
  // The line under the number. Carries the denominator or qualifier that used
  // to be crammed into the title ("Back on Task Within 2 Min" → "Back on Task"
  // + "within 2 minutes"), which is what made the titles long and the cards
  // ugly.
  caption = null,
  // Kept so callers need no edit; the tinted icon badge it used to render was
  // decoration that cost the title a line of wrapping.
  icon = null, // eslint-disable-line no-unused-vars
  // What this number assumes or excludes. On the card because the caveat has to
  // travel with the figure — this is what gets screenshotted into a board pack.
  note = null,
  onClick = null,
  actionLabel = null,
}) {
  const [showNote, setShowNote] = useState(false);
  const unavailable = !loading && unavailableLabel !== null && (value === null || value === undefined);
  const animatedValue = useCountUp(loading || unavailable ? 0 : value);
  const cardColumnProps = isVertical
    ? { xl: "12", lg: "12", md: "12", sm: "12", xs: "12" }
    : { xl: "3", lg: "6", md: "6", sm: "12", xs: "12" };

  return (
    <Col {...cardColumnProps} className="mb-3">
      <motion.div
        className={`mcap-stat ${ACCENT_BY_COLOR_CLASS[colorClass] ?? "mcap-accent-teal"}${onClick ? " is-clickable" : ""}`}
        custom={index}
        initial="hidden"
        animate="visible"
        variants={tileVariants}
        onClick={onClick ?? undefined}
        role={onClick ? "button" : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={
          onClick
            ? (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onClick();
                }
              }
            : undefined
        }
      >
        <div className="mcap-stat-top">
          <span className="mcap-stat-dot" aria-hidden="true" />
          <p className="mcap-stat-label mb-0">{label}</p>
          {note && (
            <button
              type="button"
              className="mcap-stat-info"
              aria-label={showNote ? "Hide assumption" : "What this number assumes"}
              aria-expanded={showNote}
              onClick={(e) => {
                // The card itself may be a button; without this the info
                // control would also fire the card's own action.
                e.stopPropagation();
                setShowNote((v) => !v);
              }}
            >
              <i className={`mdi ${showNote ? "mdi-close" : "mdi-information-outline"}`} />
            </button>
          )}
        </div>

        {showNote ? (
          <p className="mcap-stat-note mb-0">{note}</p>
        ) : (
          <div className="mcap-stat-figure">
            {loading ? (
              <span className="mcap-stat-skeleton" aria-hidden="true" />
            ) : unavailable ? (
              <p className="mcap-stat-unavailable mb-0">{unavailableLabel}</p>
            ) : (
              <p className="mcap-stat-value mb-0">
                {animatedValue}
                {unit && <span className="mcap-stat-unit">{unit}</span>}
                {suffix && <span className="mcap-stat-suffix">{suffix}</span>}
              </p>
            )}

            {!loading && (actionLabel || caption) && (
              <span className={actionLabel ? "mcap-stat-action" : "mcap-stat-caption"}>
                {actionLabel || caption}
                {actionLabel && <i className="mdi mdi-arrow-right" />}
              </span>
            )}
          </div>
        )}
      </motion.div>
    </Col>
  );
}
